import { useParams } from 'react-router-dom';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { useEstimateNormDetail } from '../shared/hooks/useEstimateNormsApply';

export function NormDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useEstimateNormDetail(id);

  if (isLoading) return <div className="p-6 sm:text-sm text-xs text-slate-500">Đang tải định mức...</div>;
  if (!data) return <EmptyState title="Không tìm thấy định mức" description="Định mức không tồn tại hoặc bạn chưa có quyền truy cập." />;

  const totals = data.resources.reduce<Record<string, number>>((acc, resource) => {
    acc[resource.resource_type] = (acc[resource.resource_type] || 0) + Number(resource.norm_quantity || 0);
    return acc;
  }, {});

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="shrink-0 p-4">
        <PageHeaderNew title={data.norm.norm_name} subtitle={`${data.norm.norm_code} | ${data.norm.category_code}`} backUrl="/estimates/norms" className="mb-3" />
        <div className="flex flex-wrap gap-2">
          <Badge variant="info">{data.norm.unit}</Badge>
          <Badge variant={data.norm.status_code === 'active' ? 'success' : 'neutral'}>{data.norm.status_code}</Badge>
          <Badge variant="neutral">{data.resources.length} tài nguyên</Badge>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        <div className="grid gap-4 xl:grid-cols-12 xl:items-start">
          {/* CỘT TRÁI (8) */}
          <div className="space-y-4 xl:col-span-8">
            {/* 1. Thông tin định mức */}
            <Card className="border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-medium tracking-wide text-emerald-800">Thông tin định mức</h2>
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                <div>
                  <p className="text-xs text-slate-500">Mã định mức</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{data.norm.norm_code}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Nhóm công việc</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{data.norm.category_code}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Nguồn lực</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">
                    {data.norm.norm_type === 'labor' ? 'Nhân công' : data.norm.norm_type === 'material' ? 'Vật tư' : data.norm.norm_type === 'equipment' ? 'Thiết bị' : data.norm.norm_type === 'other' ? 'Khác' : data.norm.norm_type || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Phạm vi áp dụng</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{data.norm.apply_to === 'all' ? 'Tất cả công trình' : data.norm.apply_to === 'specific' ? 'Công trình cụ thể' : '-'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-slate-500">Ghi chú</p>
                  <p className="mt-1 text-sm text-slate-900">{data.norm.notes || '-'}</p>
                </div>
                <div className="sm:col-span-2 md:col-span-3">
                  <p className="text-xs text-slate-500">Mô tả công việc</p>
                  <p className="mt-1 text-sm text-slate-900">{data.norm.description || '-'}</p>
                </div>
              </div>
            </Card>

            {/* 2. Thành phần định mức */}
            <Card className="overflow-hidden border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 p-5">
                <h2 className="text-sm font-medium tracking-wide text-emerald-800">Thành phần định mức</h2>
              </div>
              <div className="overflow-auto">
                <table className="w-full min-w-[760px] border-collapse text-left">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-3 text-center sm:text-sm text-xs font-medium text-slate-600">Loại</th>
                      <th className="p-3 text-center sm:text-sm text-xs font-medium text-slate-600">Mã</th>
                      <th className="p-3 text-left sm:text-sm text-xs font-medium text-slate-600">Tên tài nguyên</th>
                      <th className="p-3 text-center sm:text-sm text-xs font-medium text-slate-600">ĐVT</th>
                      <th className="p-3 text-right sm:text-sm text-xs font-medium text-slate-600">Hao hụt (%)</th>
                      <th className="p-3 text-right sm:text-sm text-xs font-medium text-slate-600">Định mức</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.resources.length > 0 ? (
                      data.resources.map((resource) => (
                        <tr key={resource.id} className="transition-colors hover:bg-slate-50">
                          <td className="p-3 text-center sm:text-sm text-xs text-slate-700">{resource.resource_type === 'material' ? 'Vật tư' : resource.resource_type === 'labor' ? 'Nhân công' : resource.resource_type === 'equipment' ? 'Thiết bị' : resource.resource_type === 'agro' ? 'Phân thuốc' : resource.resource_type}</td>
                          <td className="p-3 text-center sm:text-sm text-xs text-slate-600">{resource.resource_code || '-'}</td>
                          <td className="p-3 sm:text-sm text-xs font-medium text-slate-900">{resource.resource_name}</td>
                          <td className="p-3 text-center sm:text-sm text-xs text-slate-600">{resource.unit}</td>
                          <td className="p-3 text-right sm:text-sm text-xs text-slate-600">{Number(resource.waste_percent || 0).toLocaleString('vi-VN')}</td>
                          <td className="p-3 text-right sm:text-sm text-xs text-slate-600">{Number(resource.norm_quantity || 0).toLocaleString('vi-VN')}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-sm text-slate-500">
                          Chưa có tài nguyên nào
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* 3. Lịch sử cập nhật */}
            <Card className="border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-medium tracking-wide text-emerald-800">Lịch sử cập nhật</h2>
              <div className="flex flex-col items-center justify-center py-6 text-slate-400">
                <i className="ri-history-line mb-2 text-3xl"></i>
                <p className="text-sm font-medium text-slate-600">Chưa có lịch sử</p>
                <p className="mt-1 text-xs">Các thay đổi về định mức sẽ hiển thị tại đây</p>
              </div>
            </Card>
          </div>

          {/* CỘT PHẢI (4) */}
          <div className="space-y-4 xl:sticky xl:top-4 xl:col-span-4">
            {/* 1. Thông tin khác */}
            <Card className="border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-medium tracking-wide text-emerald-800">Thông tin khác</h2>
              <div className="space-y-3">
                <div className="flex justify-between border-b border-slate-50 pb-2">
                  <p className="text-xs text-slate-500">Người tạo</p>
                  <p className="text-xs font-medium text-slate-900">System Admin</p>
                </div>
                <div className="flex justify-between border-b border-slate-50 pb-2">
                  <p className="text-xs text-slate-500">Ngày tạo</p>
                  <p className="text-xs font-medium text-slate-900">{data.norm.created_at ? new Date(data.norm.created_at).toLocaleDateString('vi-VN') : '-'}</p>
                </div>
                <div className="flex justify-between border-b border-slate-50 pb-2">
                  <p className="text-xs text-slate-500">Người cập nhật</p>
                  <p className="text-xs font-medium text-slate-900">-</p>
                </div>
                <div className="flex justify-between border-b border-slate-50 pb-2">
                  <p className="text-xs text-slate-500">Ngày cập nhật</p>
                  <p className="text-xs font-medium text-slate-900">{data.norm.updated_at ? new Date(data.norm.updated_at).toLocaleDateString('vi-VN') : '-'}</p>
                </div>
                <div className="flex justify-between border-b border-slate-50 pb-2">
                  <p className="text-xs text-slate-500">Lần cập nhật</p>
                  <p className="text-xs font-medium text-slate-900">Rev. 1</p>
                </div>
                <div className="flex justify-between pb-1">
                  <p className="text-xs text-slate-500">Nguồn tham chiếu</p>
                  <p className="text-xs font-medium text-slate-900">-</p>
                </div>
              </div>
            </Card>

            {/* 2. Tổng hao phí */}
            <Card className="border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-medium tracking-wide text-emerald-800">Tổng hao phí theo loại</h2>
              <div className="grid grid-cols-2 gap-2">
                {['material', 'labor', 'equipment', 'agro'].map((type) => (
                  <div key={type} className="rounded-md border border-slate-100 bg-slate-50 p-2 text-center">
                    <p className="text-[10px] tracking-widest text-slate-500">{type === 'material' ? 'Vật tư' : type === 'labor' ? 'Nhân công' : type === 'equipment' ? 'Thiết bị' : 'Phân thuốc'}</p>
                    <p className="mt-0.5 text-sm font-medium text-slate-700">{(totals[type] || 0).toLocaleString('vi-VN')}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* 3. Đính kèm */}
            <Card className="border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-medium tracking-wide text-emerald-800">Tệp đính kèm</h2>
              <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50 py-6 text-slate-400">
                <i className="ri-attachment-line mb-2 text-3xl"></i>
                <p className="text-sm font-medium text-slate-600">Không có tệp đính kèm</p>
                <p className="mt-1 text-xs">Các bản vẽ, tài liệu sẽ hiển thị ở đây</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
